/**
 * Felles oversettelse av LMS-feilkoder til brukervennlige norske meldinger.
 * Brukes av LmsMappingModal, MoodleSettings, TutorLMSSettings, LearnDashSettings,
 * XtramileSettings og LmsConnectionStatus.
 *
 * Hver feil har:
 * - title: kort overskrift
 * - description: hva som gikk galt (1-2 setninger)
 * - guidance: hva brukeren skal gjøre for å rette det (kan være flere linjer)
 * - severity: 'error' | 'warning' (styrer farge)
 * - origin: 'user' | 'lms' | 'system' (hvem som må fikse det)
 */

export interface LmsErrorInfo {
  title: string;
  description: string;
  guidance: string;
  severity: 'error' | 'warning';
  origin: 'user' | 'lms' | 'system';
  code: string;
}

const ERROR_MAP: Record<string, Omit<LmsErrorInfo, 'code'>> = {
  // === MOODLE-SPESIFIKKE ===
  moodle_capability_missing: {
    title: 'Moodle-brukeren mangler tilgang',
    description: 'WS-brukeren (webservice-kontoen) som Kompetanseattest bruker har ikke tilstrekkelige rettigheter i Moodle. Hvis feilen sier "ikke påmeldt" eller "not enrolled" — det er Moodles formulering for "mangler rolle som dekker dette kurset". Du trenger IKKE å melde brukeren på hvert enkelt kurs.',
    guidance:
      'Anbefalt løsning (engangsjobb, ca. 30 sek):\n' +
      'Tildel WS-brukeren rollen "Manager" på System-context i Moodle:\n' +
      '   Site administration → Users → Permissions → Assign system roles → Manager → Velg WS-brukeren\n\n' +
      'Da arver brukeren tilgang til ALLE kurs automatisk, uten påmelding. Dette er standardpraksis for service-kontoer.\n\n' +
      'Restriktiv variant (kun hvis IT-policy forbyr Manager-rolle):\n' +
      'Lag en egendefinert rolle med moodle/course:view + course:viewparticipants + grade:view på både System- og Course-context, OG meld WS-brukeren på hvert kurs som skal synkes. Dette er tungvint og må gjentas for nye kurs.\n\n' +
      'Sjekk også at funksjonen "core_course_get_courses_by_field" er lagt til på Web Services-tjenesten (Site administration → Server → Web services → External services).',
    severity: 'error',
    origin: 'lms',
  },
  moodle_invalid_token: {
    title: 'Moodle-token er ugyldig eller utløpt',
    description: 'Tokenet vi bruker til å kommunisere med Moodle blir ikke akseptert av serveren.',
    guidance:
      'Gå inn på Moodle som administrator:\n' +
      '1. Site administration → Server → Web services → Manage tokens\n' +
      '2. Finn tokenet som tilhører WS-brukeren for Kompetanseattest\n' +
      '3. Slett gammelt token og generer et nytt\n' +
      '4. Lim inn det nye tokenet i Kompetanseattest under LMS-innstillinger → Moodle\n' +
      '5. Klikk "Test tilkobling" for å bekrefte at det fungerer',
    severity: 'error',
    origin: 'lms',
  },
  moodle_function_missing: {
    title: 'Påkrevd Moodle-funksjon mangler',
    description: 'Web Services-tjenesten har ikke aktivert alle funksjonene Kompetanseattest trenger.',
    guidance:
      'I Moodle: Site administration → Server → Web services → External services → velg tjenesten brukt av Kompetanseattest → Functions.\n\n' +
      'Bekreft at disse er lagt til:\n' +
      '• core_course_get_courses_by_field\n' +
      '• core_user_get_users_by_field\n' +
      '• core_completion_get_activities_completion_status\n' +
      '• gradereport_user_get_grade_items',
    severity: 'error',
    origin: 'lms',
  },

  // === TUTORLMS / LEARNDASH (WordPress-baserte) ===
  wordpress_auth_failed: {
    title: 'WordPress-pålogging feilet',
    description: 'Brukernavn eller application-passordet vi bruker for å snakke med WordPress blir avvist.',
    guidance:
      'I WordPress:\n' +
      '1. Gå til Brukere → Profil for kontoen som brukes (typisk en admin- eller manager-bruker)\n' +
      '2. Bla ned til "Application Passwords"\n' +
      '3. Tilbakekall gammelt passord og generer et nytt\n' +
      '4. Lim inn det nye applikasjonspassordet i Kompetanseattest\n' +
      '5. Klikk "Test tilkobling"',
    severity: 'error',
    origin: 'lms',
  },
  tutorlms_pro_required: {
    title: 'TutorLMS Pro kreves',
    description: 'TutorLMS sin REST API for fullføringsdata er kun tilgjengelig i Pro-lisensen.',
    guidance:
      'Kjøp TutorLMS Pro og aktiver Tutor REST API i TutorLMS-innstillingene. Gratis-versjonen mangler endpoints vi trenger for å synkronisere kursfullføringer.',
    severity: 'error',
    origin: 'lms',
  },

  // === XTRAMILE ===
  xtramile_no_valid_license: {
    title: 'Xtramile-lisensen er utløpt eller ugyldig',
    description: 'Xtramile-API-et returnerer at lisensen ikke gir tilgang til integrasjonen.',
    guidance:
      'Kontakt Xtramile-support for å verifisere lisens-status. Hvis lisensen er gyldig, kan API-nøkkelen ha blitt regenerert — be om en ny nøkkel og oppdater den i Kompetanseattest under LMS-innstillinger → Xtramile.',
    severity: 'error',
    origin: 'lms',
  },
  xtramile_api_key_invalid: {
    title: 'Xtramile API-nøkkel er ugyldig',
    description: 'Xtramile-serveren godtar ikke API-nøkkelen vi bruker.',
    guidance:
      'Be Xtramile om å regenerere API-nøkkelen, og lim den inn i Kompetanseattest under LMS-innstillinger → Xtramile. Klikk deretter "Test tilkobling".',
    severity: 'error',
    origin: 'lms',
  },

  // === STUDIEPLAN-VALIDERING (felles for alle LMS) ===
  studyplan_not_approved: {
    title: 'Studieplanen er ikke godkjent',
    description: 'Du kan ikke koble et LMS-kurs til en studieplan som ikke er godkjent ennå.',
    guidance:
      'Gå til Studieplaner-fanen, åpne studieplanen, og send den til godkjenning (eller selvgodkjenn hvis KI-validering ga ≥80 poeng). Når den er godkjent kan du opprette koblingen.',
    severity: 'error',
    origin: 'user',
  },
  studyplan_mismatch: {
    title: 'Feil studieplan-URL i kursbeskrivelsen',
    description: 'Kursbeskrivelsen i LMS-et inneholder en lenke til en annen studieplan enn den du har valgt her.',
    guidance:
      'Sjekk at lenken til studieplanen i kursbeskrivelsen i LMS-et peker på samme studieplan som du har valgt i denne dialogen. Lenken må være den offentlige verifikasjonslenken (vises på studieplanen under "Del").',
    severity: 'error',
    origin: 'user',
  },
  no_studyplan_url: {
    title: 'Ingen studieplan-lenke funnet i kursbeskrivelsen',
    description: 'Kursbeskrivelsen i LMS-et inneholder ingen lenke til en godkjent studieplan.',
    guidance:
      'Åpne kurset i LMS-et og legg til en lenke til studieplanen i kursbeskrivelsen (Summary/Description). Lenken må være den offentlige verifikasjonslenken som vises på studieplanen under "Del".',
    severity: 'error',
    origin: 'user',
  },
  no_description: {
    title: 'Kurset har ingen beskrivelse i LMS-et',
    description: 'Vi krever en kursbeskrivelse som inneholder en lenke til den godkjente studieplanen for å sikre sporbarhet.',
    guidance: 'Åpne kurset i LMS-et og fyll inn en beskrivelse (Summary/Description) med lenke til den godkjente studieplanen.',
    severity: 'error',
    origin: 'user',
  },
  missing_study_plan: {
    title: 'Mangler studieplan',
    description: 'Du må velge en studieplan før du kan opprette koblingen.',
    guidance: 'Velg en godkjent studieplan i dropdown-listen øverst i dialogen.',
    severity: 'warning',
    origin: 'user',
  },

  // === SYSTEM-/INFRASTRUKTUR-FEIL ===
  lms_fetch_failed: {
    title: 'Kunne ikke hente data fra LMS-et',
    description: 'Vi klarte ikke å kommunisere med LMS-serveren — det kan være nettverksproblem, server nede, eller endret URL.',
    guidance:
      'Sjekk at LMS-URL-en er riktig konfigurert under LMS-innstillinger. Hvis URL-en stemmer, prøv "Test tilkobling" der. Hvis det fortsatt feiler, ta kontakt med LMS-administrator for å verifisere at serveren er tilgjengelig.',
    severity: 'error',
    origin: 'system',
  },
  network_error: {
    title: 'Nettverksfeil',
    description: 'Forbindelsen brøt eller serveren svarte ikke i tide.',
    guidance: 'Prøv igjen om noen sekunder. Hvis problemet vedvarer, kan det være en midlertidig serverfeil.',
    severity: 'warning',
    origin: 'system',
  },
};

/**
 * Slå opp feilinfo for en feilkode. Hvis koden ikke er kjent, returnerer
 * vi en generisk feilmelding med koden for support-bruk.
 */
export function getLmsErrorInfo(code: string | null | undefined, fallbackMessage?: string): LmsErrorInfo {
  if (!code) {
    return {
      code: 'unknown',
      title: 'Ukjent feil',
      description: fallbackMessage || 'En ukjent feil oppstod.',
      guidance: 'Prøv igjen, eller ta kontakt med support hvis problemet vedvarer.',
      severity: 'error',
      origin: 'system',
    };
  }

  const info = ERROR_MAP[code];
  if (!info) {
    return {
      code,
      title: 'Feil ved kommunikasjon med LMS-et',
      description: fallbackMessage || `Feilkode: ${code}`,
      guidance: `Oppgi feilkoden "${code}" til support hvis du trenger hjelp med å forstå hva som gikk galt.`,
      severity: 'error',
      origin: 'system',
    };
  }

  return { code, ...info };
}

/**
 * Detekter feilkode fra fri-tekst-feilmelding (når backend ikke gir code-felt).
 * Brukes som fallback når vi bare har en error-string.
 */
export function inferLmsErrorCode(message: string): string | null {
  if (!message) return null;
  const m = message.toLowerCase();

  // Moodle
  if (m.includes('ugyldig token') || m.includes('invalid token') || m.includes('tokenet kunne ikke finnes')) return 'moodle_invalid_token';
  if (m.includes('unntak fra tilgangskontroll') || m.includes('access control exception') || m.includes('kurskonteksten') || m.includes('nopermissions') || m.includes('require_capability') || m.includes('webservice_access_exception') || m.includes('ikke påmeldt') || m.includes('not enrolled') || m.includes('not have permission') || m.includes('ingen påmelding') || m.includes('brukeren er ikke')) return 'moodle_capability_missing';
  if (m.includes('function') && m.includes('not exist')) return 'moodle_function_missing';

  // WordPress
  if (m.includes('application password') || (m.includes('401') && m.includes('wordpress'))) return 'wordpress_auth_failed';
  if (m.includes('tutorlms pro') || m.includes('tutor pro required')) return 'tutorlms_pro_required';

  // Xtramile
  if (m.includes('no_valid_license') || m.includes('lisens')) return 'xtramile_no_valid_license';
  if (m.includes('api key') || m.includes('api-nøkkel')) return 'xtramile_api_key_invalid';

  // Studieplan-validering
  if (m.includes('studyplan_not_approved') || (m.includes('studieplan') && m.includes('ikke godkjent'))) return 'studyplan_not_approved';
  if (m.includes('studyplan_mismatch') || (m.includes('studieplan') && (m.includes('peker') || m.includes('annen')))) return 'studyplan_mismatch';
  if (m.includes('no_studyplan_url') || m.includes('ingen studieplan-lenke')) return 'no_studyplan_url';
  if (m.includes('no_description') || m.includes('ingen beskrivelse')) return 'no_description';

  // Nettverk / generelle
  if (m.includes('econnref') || m.includes('etimedout') || m.includes('enotfound') || m.includes('timeout')) return 'network_error';
  if (m.includes('lms_fetch_failed') || (m.includes('kunne ikke hente') && m.includes('lms'))) return 'lms_fetch_failed';

  return null;
}
